import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Achievement.css";

import coinIcon from "../../assets/coin.png";
import { TbLock, TbInfoCircle } from "react-icons/tb";
import styled from "styled-components";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

interface PointsInfo {
  user_points_balance: string;
  points_map: {
    Lifetime_points: number;
    Active_points: number;
    Expired_points: number;
  }
}

interface Badge {
  badge_uid: string;
  badge_name: string;
  badge_category: string;
  badge_description: string;
  badge_type: string;
  badge_image: string;
  badge_points?: number;
}

const Achievement: React.FC = () => {
  useCheckSessionHeartBeat();

  const [orgBadges, setOrgBadges] = useState<Badge[]>([]);
  const [achievedBadges, setAchievedBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const API_URL_ORGINFO = `${process.env.REACT_APP_API_URL}/api/points`;
  const API_URL_BADGEINFO = `${process.env.REACT_APP_API_URL}/api/badges`;

  const [balancePoints, setBalancePoints] = useState<PointsInfo>({
    user_points_balance: "",
    points_map: {
      Lifetime_points: 0,
      Active_points: 0,
      Expired_points: 0,
    }
  });

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [levelData, setLevelData] = useState<any>(null);

  const userUid = localStorage.getItem("user_uid") ?? "";

  // --- API CALLS ---
  useEffect(() => {
    const fetchPoints = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_URL_ORGINFO, {
          headers: { Accept: "application/json", "user-uid": userUid },
          withCredentials: true,
        });
        if (response.status === 200) {
          setBalancePoints({
            user_points_balance: response.data.data.user_points_balance.toString(),
            points_map: response.data.data.points_map,
          });
        }
      } catch (error) {
        console.error("Error fetching points:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPoints();
  }, [userUid]);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const response = await axios.get(API_URL_BADGEINFO, {
          headers: { Accept: "application/json", "user-uid": userUid },
          withCredentials: true,
        });
        if (response.status === 200) {
          const { org_badges, achieved_badges } = response.data.data;
          setOrgBadges(org_badges || []);
          setAchievedBadges(achieved_badges || []);
        }
      } catch (error) {
        console.error("Error fetching badge info:", error);
      }
    };
    fetchBadges();
  }, [userUid]);

  useEffect(() => {
    const fetchLevelData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/my-level`, {
          headers: { Accept: "application/json", "user-uid": userUid },
          withCredentials: true,
        });
        if (response.status === 200 && response.data?.data) {
          setLevelData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching level data:", error);
      }
    };
    fetchLevelData();
  }, [userUid]);

  const openBadgeModal = (badge: any) => {
    setSelectedBadge(badge);
    setModalOpen(true);
  };

  const closeBadgeModal = () => {
    setModalOpen(false);
    setSelectedBadge(null);
  };

  // --- Logic Helpers ---
  const getLevelDisplayData = () => {
    if (!levelData) return null;
    const current = levelData.curr_level_progress_xp || 0;
    const estimated = levelData.curr_level_estimated_xp || 0;

    // If estimated is 0, we assume it's max level, so max is current
    const effectiveMax = estimated === 0 ? current : estimated;
    const percentage = effectiveMax === 0 ? 100 : Math.min((current / effectiveMax) * 100, 100);

    return { current, effectiveMax, percentage };
  };

  const levelDisplay = getLevelDisplayData();

  return (
    <div className="achievement-page">
      <section className="achievement-progress">
        <div className="achievement-head-progress">My Progress</div>
        <div className="achievement-progress-header">

          {/* --- LEFT SIDE: LEVEL --- */}
          <div className="achievement-progress-one">
            <div className="progress-container">
              {levelData && levelDisplay ? (
                <>
                  <div className="achievement-level-progress-header">
                    Level {levelData.current_level} - {levelData.level_character}
                  </div>
                  <div className="progress-subtext">
                    {levelData.xp_needed_for_next_level > 0
                      ? `${levelData.xp_needed_for_next_level} XP to next level`
                      : `Max level reached!`}
                  </div>
                  <div className="progress-bar-wrapper">
                    <div className="progress-circle">{levelData.current_level}</div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${levelDisplay.percentage}%`,
                          minWidth: levelDisplay.percentage < 10 ? "0px" : "auto",
                          display: "flex",
                          justifyContent: levelDisplay.percentage < 10 ? "left" : "right",
                          alignItems: "center",
                          color: "#a0b0a0",
                          padding: "2px 0px",
                          position: "relative",
                        }}
                      >
                        {levelDisplay.percentage < 10 ? (
                          <span
                            style={{
                              position: "absolute",
                              left: "105%",
                              whiteSpace: "nowrap",
                              color: "var(--text-color)",
                            }}
                          >
                            ⭐ {levelDisplay.current}/{levelDisplay.effectiveMax}
                          </span>
                        ) : (
                          `⭐ ${levelDisplay.current}/${levelDisplay.effectiveMax}`
                        )}
                      </div>
                    </div>
                    <div className="progress-circle">{levelData.next_level ?? "🏆"}</div>
                  </div>
                </>
              ) : (
                <div style={{ padding: '0 5%' }}>Loading progress...</div>
              )}
            </div>
          </div>

          {/* --- RIGHT SIDE: POINTS --- */}
          <div onClick={() => setShowPointsModal(true)} style={{ cursor: "pointer", height: "100%" }}>
            <div className="achievement-progress-two">
              <div className="achievement-points-card">
                <div className="achievement-points-image">
                  <img src={coinIcon} alt="Coin Icon" />
                </div>
                <div className="achievement-points-content">
                  <div className="achievement-points-label">Points Balance</div>
                  <div className="achievement-points-value">
                    {balancePoints.user_points_balance || "0"}
                  </div>
                </div>
              </div>
              <div className="achievement-points-hint">Tap for details</div>
            </div>
          </div>

        </div>
      </section>

      {/* --- BADGES SECTION --- */}
      <section className="achievement-badges">
        <div className="achievement-head-progress">My Badges</div>

        <div className="badge-grid">
          {orgBadges.length === 0 ? (
            <div className="badge-message">No badges available.</div>
          ) : (
            (() => {
              const sortedBadges = [...orgBadges].sort((a, b) => {
                const isAchievedA = achievedBadges.some(ach => ach.badge_uid === a.badge_uid);
                const isAchievedB = achievedBadges.some(ach => ach.badge_uid === b.badge_uid);

                if (isAchievedA && !isAchievedB) return -1;
                if (!isAchievedA && isAchievedB) return 1;
                return 0;
              });

              const hasAchieved = sortedBadges.some((badge) =>
                achievedBadges.some((ach) => ach.badge_uid === badge.badge_uid)
              );

              // Logic for separator
              let lockedSeparatorShown = false;

              return (
                <>
                  {/* Message for NO earned badges */}
                  {!hasAchieved && (
                    <div className="badge-message-row">
                      You haven’t earned any badges yet.
                    </div>
                  )}

                  {sortedBadges.map((badge) => {
                    const isAchieved = achievedBadges.some(ach => ach.badge_uid === badge.badge_uid);

                    // Logic to show separator only once when switching from achieved to locked
                    // OR if no badges achieved, show it at the very top (optional, but requested logic implies we just show locked badges below)
                    // If we want the separator line specifically:
                    const showLockedSeparator = !isAchieved && !lockedSeparatorShown && hasAchieved;

                    if (showLockedSeparator) {
                      lockedSeparatorShown = true;
                    }

                    return (
                      <React.Fragment key={badge.badge_uid}>
                        {showLockedSeparator && (
                          <div className="badge-separator">
                            <span>Locked Badges</span>
                          </div>
                        )}

                        <div
                          className={`badge-card ${isAchieved ? "achieved" : "locked"}`}
                          onClick={() => openBadgeModal(badge)}
                        >
                          <div className="badge-image-wrapper">
                            <img
                              src={`data:image/jpeg;base64,${badge.badge_image}`}
                              alt={badge.badge_name}
                              className="badge-image"
                            />
                            {!isAchieved && (
                              <div className="lock-icon-overlay"><TbLock /></div>
                            )}
                          </div>
                          <div className="badge-name">{badge.badge_name}</div>
                          <div className={`badge-status ${isAchieved ? "unlocked" : "locked"}`}>
                            {isAchieved ? "Unlocked" : "Locked"}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              );
            })()
          )}
        </div>
      </section>

      {/* --- BADGE MODAL --- */}
      {modalOpen && selectedBadge && (
        <div className="achievement-modal">
          <div className="achievement-modal-content">
            <span className="achievement-close-btn" onClick={closeBadgeModal}>X</span>

            <div className="badge-modal-header">
              <div className="badge-modal-icon-container">
                <img
                  src={`data:image/jpeg;base64,${selectedBadge?.badge_image}`}
                  alt={selectedBadge?.badge_name}
                />
              </div>
              <h3>{selectedBadge?.badge_name}</h3>
              <span className="badge-category-pill">{selectedBadge?.badge_category}</span>
            </div>

            <div className="badge-description-box">
              <p>Description: {selectedBadge?.badge_description}</p>
            </div>

            <div className="badge-stats-row">
              <div className="badge-stat">
                <span className="stat-label">Type</span>
                <span className="stat-val">{selectedBadge?.badge_type}</span>
              </div>
              <div className="badge-stat">
                <span className="stat-label">Points</span>
                <span className="stat-val">{selectedBadge?.badge_points || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- POINTS MODAL --- */}
      {showPointsModal && (
        <ModalOverlay onClick={() => setShowPointsModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowPointsModal(false)}>&times;</CloseButton>

            <ModalHeader>
              <img src={coinIcon} alt="Coin" style={{ width: '40px' }} />
              <div>
                <ModalTitle>Points Wallet</ModalTitle>
                <ModalSubtitle>Summary of your rewards</ModalSubtitle>
              </div>
            </ModalHeader>

            <BalanceCard>
              <span className="label">Current Balance</span>
              <span className="value">{balancePoints.user_points_balance}</span>
            </BalanceCard>

            <StatsGrid>
              <StatBox type="lifetime">
                <div className="val">{balancePoints.points_map.Lifetime_points}</div>
                <div className="lbl">Lifetime Earned</div>
              </StatBox>
              <StatBox type="active">
                <div className="val">{balancePoints.points_map.Active_points}</div>
                <div className="lbl">Active Points</div>
              </StatBox>
              <StatBox type="expired">
                <div className="val">{balancePoints.points_map.Expired_points}</div>
                <div className="lbl">Expired Points</div>
              </StatBox>
            </StatsGrid>

          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  );
};

export default Achievement;

// --- STYLED COMPONENTS ---

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--page-color, #fff);
  color: var(--text-color, #333);
  padding: 24px;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--text-color, #333);
  cursor: pointer;
  opacity: 0.6;
  &:hover { opacity: 1; }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  padding-bottom: 15px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
`;

const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.7;
`;

const BalanceCard = styled.div`
  background: var(--sidebar-hover-color, #e0f7fa);
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  
  .label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 5px;
    opacity: 0.8;
  }
  .value {
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const StatBox = styled.div<{ type: string }>`
  background: rgba(0,0,0,0.03);
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid rgba(0,0,0,0.05);

  .val {
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 4px;
    color: ${props =>
    props.type === 'active' ? '#2e7d32' :
      props.type === 'expired' ? '#c62828' : 'inherit'};
  }
  .lbl {
    font-size: 0.7rem;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;